import { FieldValidator } from 'final-form'
import countries from 'i18n-iso-countries'
import { concat, difference, isArray, keys, sortBy } from 'lodash'
import React, { useMemo } from 'react'
import { components, InputProps } from 'react-select'
import { CSSObject } from 'styled-components'

import Field from '../Field'
import { TField } from '../Field/types'
import Select from '../Select'
import {
  dachList,
  europeList,
  getIcon,
  makeOptions,
  parseCountry,
  stringifyCountry,
  TCountryOptionitem,
} from './utils'

const Input = (props: InputProps): React.ReactElement => (
  <components.Input {...props} autoComplete="foo" />
)

type TCountryOption = {
  label: string
  value?: string
  options?: TCountryOptionitem[]
}
export interface TCountryPicker extends Omit<TField, 'name'> {
  /** Hide the label of the field */
  hideLabel?: boolean

  /** Allow select multiple option */
  multi?: boolean

  /** Name of the field */
  field?: string

  /** Field validation */
  validation?: FieldValidator<any>

  /** Render smaller field */
  small?: boolean

  /** Direct prop of <Select /> component */
  direct?: boolean

  /** Required a value to be selected */
  required?: boolean

  /** Render without <Field /> component of final-form */
  withoutField?: boolean

  /** Disable select */
  disabled?: boolean
  customOptions?: TCountryOption[]
  customPlaceHolder?: string
  hideCountryText?: boolean
  useShortCode?: boolean
  isClearable?: boolean
  clearable?: boolean
}
const CountryPicker = (props: TCountryPicker): React.ReactElement => {
  const {
    hideLabel,
    multi,
    field = 'country',
    validation,
    small,
    direct,
    required = false,
    withoutField,
    disabled = false,
    customOptions = undefined,
    customPlaceHolder,
    hideCountryText = false,
    useShortCode = false,
    isClearable,
    clearable,
    ...rest
  } = props

  const language = useMemo(() => i18n.language, [])

  const options: TCountryOption[] = useMemo(() => {
    const otherList = difference(
      keys(countries.getAlpha2Codes()),
      concat(dachList, europeList),
    )
    return [
      {
        label: i18n.t('region_dach', 'DACH'),
        options: sortBy(
          makeOptions(dachList, language, useShortCode, hideCountryText),
          'label',
        ),
      },
      {
        label: i18n.t('region_europe', 'Europa'),
        options: sortBy(
          makeOptions(europeList, language, useShortCode, hideCountryText),
          'label',
        ),
      },
      {
        label: i18n.t('region_worldwide', 'Weltweit'),
        options: sortBy(
          makeOptions(otherList, language, useShortCode, hideCountryText),
          'label',
        ),
      },
    ]
  }, [language])

  const valueRenderer = ({
    label,
    value = '',
  }: TCountryOptionitem): React.ReactElement => {
    return (
      <div>
        <i className={`flag-icon flag-icon-${getIcon(value)}`} />
        {label && <span> {label}</span>}
      </div>
    )
  }

  const smallStyles = {
    control: (provided: CSSObject) => ({
      ...provided,
      height: '31',
      minHeight: '31',
    }),
    valueContainer: (provided: CSSObject) => ({
      ...provided,
      top: '-10%',
    }),
  }

  const sharedProps = {
    formatOptionLabel: valueRenderer,
    options: (isArray(customOptions) ? customOptions : options) as any,
    components: { Input },
    styles: small ? smallStyles : {},
    direct,
    isMulti: multi,
    isDisabled: disabled,
    disabled,
    searchable: true,
    clearable: clearable || isClearable,
    placeholder: customPlaceHolder || i18n.t('selectDotDotDot', 'Wählen...'),
  }

  if (withoutField) {
    return <Select {...sharedProps} {...rest} />
  }

  return (
    <Field
      label={!hideLabel}
      name={field}
      field={field}
      required={required}
      validate={validation}
      {...rest}
    >
      <Select {...sharedProps} />
    </Field>
  )
}

export default CountryPicker

export { parseCountry, stringifyCountry }
